import React, { useEffect, useRef, useTransition } from 'react';
import {useStore} from "./useWebViewStore";
import InstanceFactory from './AppFactory';

interface WebView {
    id: string;
    url: string;
    name: string;
    active: boolean;
    deepLink?: string;
}

interface WebViewWrapperProps {
    webview: WebView;
    isActive: boolean;
    onMount: (id: string, url: string, element: HTMLIFrameElement) => void;
    onReady: (id: string, url: string, element: HTMLIFrameElement) => void;
}

const WebViewWrapper = React.memo(({ 
    webview, 
    isActive, 
    onMount ,
    onReady
}:WebViewWrapperProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const instanceRef = useRef<HTMLElement | null>(null);
    const [isLoaded, setIsLoaded] = React.useState(false);
    const {setIsLoading} = useStore() as any;
    const [, startTransition] = useTransition();
    
    useEffect(() => {
        let mounted = true;
        let instance: HTMLElement | null = null;
        console.log(`Initializing webview`, webview);

        const initializeWebView = async () => {
            if (instanceRef.current) {
                setIsLoading(false);
                setIsLoaded(true);
                return; // Already initialized
            }

            console.log('webview.deepLink', webview.deepLink, webview);
        
            try {
                const factory = InstanceFactory();
            
                instance = await factory.getInstance(
                    webview.id,
                    webview.name,
                    webview.deepLink || webview.url,
                    () => {
                        if (mounted) {
                            console.log(`initializeWebView, webview - `, webview);
                            // Defer state updates to allow navigation to proceed
                            startTransition(() => {
                                setIsLoading(false);
                                setIsLoaded(true);
                                instance && onReady(webview.id, webview.deepLink || webview.url, instance as HTMLIFrameElement)
                            });
                        } 
                    }
                );

                if (mounted && wrapperRef.current && instance) {
                    instanceRef.current = instance;
                    wrapperRef.current.appendChild(instance);
                    // Defer mount callback to prevent blocking navigation
                    startTransition(() => {
                        onMount(webview.id, webview.deepLink || webview.url, instance as HTMLIFrameElement);
                    });
                }
            } catch (error) {
                console.error(`Failed to initialize webview ${webview.id}:`, error);
            }
        };

        // Defer initialization to avoid blocking navigation
        startTransition(() => {
            initializeWebView();
        });

        return () => {
            mounted = false;
            // Cleanup instance when component unmounts
            if (instanceRef.current) {
                instanceRef.current.remove();
                instanceRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webview.id, webview.name, webview.url, webview.deepLink, onMount, startTransition]);

    return (
        <div
            ref={wrapperRef}
            id={`${webview.id}-warp`}
            data-frmid={webview.id}
            className={isActive ? 'maximized' : 'minimized'}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                visibility: isActive ? 'visible' : 'hidden',
                opacity: isLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
                zIndex: isActive ? 10 : 1
            }}
        />
    );
});

export default WebViewWrapper;